import { Controller } from "@hotwired/stimulus"
import { Dropdown, Tooltip } from "bootstrap"
import { clearSidebarCollapsedTooltips } from "../utils/sidebar_collapsed_tooltips"

// Le bouton d'allumage qu'on vient de basculer garde l'aspect de son nouvel état tant que le
// pointeur est dessus (cf. `--toggled` dans _sidebar_env_switcher.scss) ; il retrouve son survol
// normal quand le pointeur le quitte.
function settlePowerButton(event) {
  const button = event.target.closest?.(".sidebar-env-switcher__power--toggled")
  if (!button || button.contains(event.relatedTarget)) return

  button.classList.remove("sidebar-env-switcher__power--toggled")
}

// Une infobulle Bootstrap ouverte sur une ligne (temps restant d'une review app) ne se ferme pas
// d'elle-même quand le menu se ferme : sa ligne disparaît sans `mouseleave`.
function hideTooltips(event) {
  event.currentTarget
    .querySelectorAll('[data-bs-toggle="tooltip"]')
    .forEach((element) => Tooltip.getInstance(element)?.hide())
}

// Menu des environnements de la barre latérale (cf. EnvSwitcherComponent). Les sections qui
// coûtent un appel — l'API Heroku, /proc — ne sont demandées qu'à l'ouverture, et redemandées
// à chaque ouverture : le menu sert justement à voir ce qui tourne. D'une ouverture à l'autre,
// l'ancienne liste reste affichée le temps que la nouvelle arrive.
//
// Pas une turbo-frame paresseuse : common.ts fige les frames une fois chargées et les greffe
// d'une page à l'autre (initializeLazyTurboFrames), la liste ne se serait plus jamais mise à
// jour.
export default class extends Controller {
  static targets = ["toggle", "section", "domains"]
  static values = { error: String }

  connect() {
    this.refresh = this.refresh.bind(this)
    this.resetBeforeCache = this.resetBeforeCache.bind(this)
    // Par élément et pas par id : les dépliés de domaines n'ont d'id que sur leur enveloppe.
    this.placeholders = new Map(this.loadableTargets.map((element) => [element, element.innerHTML]))

    this.element.addEventListener("show.bs.dropdown", this.refresh)
    this.element.addEventListener("hide.bs.dropdown", hideTooltips)
    this.element.addEventListener("pointerout", settlePowerButton)
    // Barre repliée : l'infobulle du nom de l'environnement ne doit pas rester sur le menu.
    this.element.addEventListener("shown.bs.dropdown", clearSidebarCollapsedTooltips)
    document.addEventListener("turbo:before-cache", this.resetBeforeCache)
  }

  disconnect() {
    this.element.removeEventListener("show.bs.dropdown", this.refresh)
    this.element.removeEventListener("hide.bs.dropdown", hideTooltips)
    this.element.removeEventListener("pointerout", settlePowerButton)
    this.element.removeEventListener("shown.bs.dropdown", clearSidebarCollapsedTooltips)
    document.removeEventListener("turbo:before-cache", this.resetBeforeCache)
    this.abortController?.abort()
    this.domainsAbortController?.abort()
  }

  get loadableTargets() {
    return [...this.sectionTargets, ...this.domainsTargets]
  }

  refresh() {
    this.abortController?.abort()
    this.abortController = new AbortController()
    const { signal } = this.abortController

    this.sectionTargets.forEach((section) => this.load(section, signal))
  }

  // Dépli d'un environnement : ses autres domaines, demandés au premier dépli seulement. Ils ne
  // bougent pas d'une ouverture du menu à l'autre (le serveur les garde une heure).
  toggleUnassigned(event) {
    const button = event.currentTarget
    const panel = document.getElementById(button.getAttribute("aria-controls"))
    if (!panel) return

    const expanded = button.getAttribute("aria-expanded") !== "true"
    button.setAttribute("aria-expanded", String(expanded))
    panel.dataset.expanded = String(expanded)
  }

  toggleDomains(event) {
    const button = event.currentTarget
    const panel = document.getElementById(button.getAttribute("aria-controls"))
    if (!panel) return

    const expanded = button.getAttribute("aria-expanded") !== "true"
    button.setAttribute("aria-expanded", String(expanded))
    panel.dataset.expanded = String(expanded)

    const body = this.domainsTargets.find((target) => panel.contains(target))
    if (!expanded || !body || body.dataset.loaded) return

    body.dataset.loaded = "true"
    this.domainsAbortController ??= new AbortController()
    this.load(body, this.domainsAbortController.signal).then((loaded) => {
      if (!loaded) delete body.dataset.loaded
    })
  }

  // Un favicon qui ne se charge pas (certificat, domaine injoignable) : le globe par défaut.
  faviconFailed(event) {
    const globe = document.createElement("i")
    globe.className = "fa-solid fa-globe"
    globe.setAttribute("aria-hidden", "true")
    event.target.replaceWith(globe)
  }

  async load(target, signal) {
    target.setAttribute("aria-busy", "true")

    try {
      const response = await fetch(target.dataset.src, { headers: { Accept: "text/html" }, signal })
      if (!response.ok) throw new Error(`HTTP ${response.status}`)

      target.innerHTML = await response.text()
      return true
    } catch (error) {
      if (!signal.aborted) this.showError(target)
      return false
    } finally {
      if (!signal.aborted) target.removeAttribute("aria-busy")
    }
  }

  // Les silhouettes laissent la place au message : une liste qui ne vient pas ne doit pas avoir
  // l'air de charger indéfiniment.
  showError(target) {
    target.innerHTML = this.placeholders.get(target) ?? ""
    target.querySelector("[data-env-switcher-skeleton]")?.remove()

    const status = target.querySelector("[data-env-switcher-status]")
    if (!status) return

    status.textContent = this.errorValue
    status.hidden = false
  }

  // Le cache de Turbo (le tableau de bord) ne doit garder ni un menu ouvert, ni une liste qui
  // ne dit plus ce qui tourne, ni un dépli ouvert.
  resetBeforeCache() {
    this.abortController?.abort()
    this.domainsAbortController?.abort()
    this.domainsAbortController = null
    if (this.hasToggleTarget) Dropdown.getInstance(this.toggleTarget)?.hide()

    this.loadableTargets.forEach((target) => {
      target.innerHTML = this.placeholders.get(target) ?? ""
      target.removeAttribute("aria-busy")
      delete target.dataset.loaded
    })
    this.element.querySelectorAll(".sidebar-env-switcher__expand[aria-expanded='true']").forEach((button) => {
      button.setAttribute("aria-expanded", "false")
    })
    this.element.querySelectorAll(".sidebar-env-switcher__domains").forEach((panel) => {
      panel.dataset.expanded = "false"
    })
  }
}
